import React, { useContext, useMemo, useState } from "react";
import {
	AttachmentDocumentName,
	GetAttachmentImagePath,
} from "helpers/enums-helper";
import { Button, Table, OverflowTooltip } from "components";
import useDocumentsService from "./useDocumentsService";
import DocumentsServiceContext from "./documentsServiceContext";

import { ReactComponent as DownloadImage } from "static/images/downloadFile.svg";
import { ReactComponent as ShowImage } from "static/images/eye.svg";
import { ReactComponent as RemoveImage } from "static/images/cancel.svg";

import clsx from "clsx";
import styles from "./documentsTable.module.scss";
import moment from "moment";

const DocumentsTable = ({
	entityId,
	AddDocumentButton,
	sectionClassName,
	sectionTitle,
	onDeleteDocument,
	onViewDocument,
	onDownloadDocument,
}) => {
	const [sortBy, setSortBy] = useState({ id: "createdAt", desc: false });
	const [page, setPage] = useState(0);

	const { key, service } = useContext(DocumentsServiceContext);
	const {
		useDocumentsListQuery,
		useAddDocumentMutation,
		useDeleteDocumentMutation,
		useViewDocumentMutation,
		useDownloadDocumentMutation,
	} = useDocumentsService({ key, service });

	const { data, isFetching, refetch } = useDocumentsListQuery({
		entityId,
		page,
		sortBy,
	});

	const { mutateAsync: addDocument } = useAddDocumentMutation(page, sortBy);
	const { mutateAsync: deleteDocument } = useDeleteDocumentMutation(refetch);
	const { mutateAsync: viewDocument } = useViewDocumentMutation();
	const { mutateAsync: downloadDocument } = useDownloadDocumentMutation();

	const meta = useMemo(
		() => ({
			loading: isFetching,
			totalItems: data?.count || 0,
		}),
		[isFetching, data?.count],
	);

	const tableData = useMemo(() => {
		if (Array.isArray(data)) {
			return data;
		} else if (data?.items) {
			return data.items;
		} else {
			return [];
		}
	}, [data]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const itemCount = numberItem => {
		if (page === 0) return numberItem;
		else if (page > 0) return 50 * page + numberItem;
	};

	const columns = useMemo(
		() => [
			{
				Header: "",
				accessor: "number",
				disableSortBy: true,
				classNameCell: styles.numberCol,
				classNameHeader: styles.numberCol,
				Cell({ cell, row }) {
					return <div>{itemCount(parseInt(row.id) + 1)}</div>;
				},
			},
			{
				Header: "",
				accessor: "iconType",
				disableSortBy: true,
				classNameCell: styles.iconTypeCol,
				classNameHeader: styles.iconTypeCol,
				Cell({ cell, row }) {
					return (
						<img
							src={GetAttachmentImagePath(row.original.type)}
							alt=""
							width="20"
							height="15"
							className="mr-3 pl-1"
						/>
					);
				},
			},
			{
				Header() {
					return <div>Name</div>;
				},
				accessor: "name",
				disableSortBy: true,
				Cell({ cell, row: { original } }) {
					return (
						<div
							className={`cellWithIcon cut-text-laptop ${
								original?.deleted_at ? styles.crossed : ""
							}`}
						>
							<OverflowTooltip onlyOnLaptop title={cell?.value}>
								{cell?.value}
							</OverflowTooltip>
						</div>
					);
				},
			},
			{
				Header: "Type",
				accessor: "type",
				disableSortBy: true,
				Cell({ cell }) {
					return (
						<OverflowTooltip
							onlyOnLaptop
							title={AttachmentDocumentName(cell.value)}
						>
							{AttachmentDocumentName(cell.value)}
						</OverflowTooltip>
					);
				},
			},
			{
				Header: "Timestamp",
				accessor: "created_at",
				id: "createdAt",
				Cell({ cell }) {
					const FORMAT = "DD MMMM YYYY h:mm A";
					return (
						<OverflowTooltip
							onlyOnLaptop
							title={moment
								.utc(cell.value)
								.local()
								.format(FORMAT)}
						>
							{moment.utc(cell.value).local().format(FORMAT)}
						</OverflowTooltip>
					);
				},
			},
			{
				Header() {
					return <div>Actions</div>;
				},
				accessor: "",
				id: "documentActions",
				disableSortBy: true,
				Cell({ row: { original } }) {
					return (
						<div>
							<Button
								onClick={() =>
									onViewDocument({
										document: original,
										viewDocument,
									})
								}
								icon={<ShowImage />}
								btnIcon
								variant="secondary"
								title="View Document"
							/>
							<Button
								onClick={() =>
									onDownloadDocument({
										document: original,
										downloadDocument,
									})
								}
								icon={<DownloadImage />}
								btnIcon
								variant="secondary"
								title="Download Document"
							/>
							<Button
								onClick={() =>
									onDeleteDocument({
										document: original,
										deleteDocument,
									})
								}
								icon={<RemoveImage />}
								btnIcon
								variant="secondary"
								title="Remove Document"
								size="sm"
								disabled={original?.deleted_at}
							/>
						</div>
					);
				},
			},
		],
		[
			itemCount,
			onViewDocument,
			viewDocument,
			onDownloadDocument,
			downloadDocument,
			onDeleteDocument,
			deleteDocument,
		],
	);

	const handlePageChange = ({ pageIndex }) => {
		if (pageIndex !== page) {
			setPage(pageIndex);
		}
	};

	const handleSortChange = ({ sortBy }) => {
		setSortBy(sortBy[0]);
	};

	return (
		<div className={clsx(styles.wrappAttachments, sectionClassName)}>
			<div className={styles.sectionActions}>
				{sectionTitle && (
					<h6 className={styles.sectionTitle}>{sectionTitle}</h6>
				)}
				{AddDocumentButton && (
					<AddDocumentButton addDocument={addDocument} />
				)}
			</div>
			<Table
				meta={meta}
				className="vh-80"
				columns={columns}
				data={tableData}
				handlePageChange={handlePageChange}
				handleSortChange={handleSortChange}
				initialState={{ sortBy: [sortBy], pageIndex: page }}
				compact
			/>
		</div>
	);
};

export default DocumentsTable;
